import { cachedFetch, endpointWrapper } from './meta.api';
import { fetchKeywords } from './categories.api';
import kebabCase from 'lodash/kebabCase';
import { shapePosts } from './tiktok.api';
import { shapeTranslations } from 'utils/entities';

const shapeFlavors = (rawFlavors) => {
    const flavorsOptions = [];
    const groupedFlavorsOptions = {};
    const catTwoLabels = {};

    Object.entries(rawFlavors).forEach(([catTwo, catThreeObj]) => {
        const catTwoKey = kebabCase(catTwo);
        catTwoLabels[catTwoKey] = catTwo;
        groupedFlavorsOptions[catTwoKey] = [];

        Object.entries(catThreeObj).forEach(([catThree, catFours]) => {
            const catThreeKey = kebabCase(catThree);

            const levelThreeOption = {
                label: catThree,
                value: kebabCase(`${catTwo}>${catThree}`),
                catTwo: catTwoKey,
                catThree: catThreeKey,
                depth: 3,
            };

            const catFourList = catFours.map((catFour) => ({
                label: catFour,
                value: kebabCase(`${catTwo}>${catThree}>${catFour}`),
                catTwo: catTwoKey,
                catThree: catThreeKey,
                depth: 4,
            }));

            flavorsOptions.push(levelThreeOption, ...catFourList);

            groupedFlavorsOptions[catTwoKey].push({
                ...levelThreeOption,
                catFourList,
            });
        });

        const allCatTwoOption = {
            label: `All ${catTwo}`,
            value: kebabCase(`all-${catTwo}`),
            catTwo: catTwoKey,
            catFourList: [],
            depth: 3,
        };

        flavorsOptions.push(allCatTwoOption);
        groupedFlavorsOptions[catTwoKey].push(allCatTwoOption);
    });

    return {
        flavorsOptions,
        groupedFlavorsOptions,
        catTwoLabels,
    };
};

export const getFlavors = async (config) => {
    const response = await cachedFetch(endpointWrapper)({
        domain: 'flavors',
        ...config,
    });
    return shapeFlavors(response);
};

const validDataSources = ['tiktok', 'google', 'pop_index'];

export const getFlavorsByCategory = async ({
    category,
    country,
    order_by,
    ascending,
    dataSource,
    group,
    search,
}) => {
    if (!validDataSources.includes(dataSource)) {
        throw new Error(`Invalid data source: ${dataSource}`);
    }
    return cachedFetch(endpointWrapper)({
        domain: `${dataSource}/flavors/flavors`,
        data: {
            category,
            country,
            order_by,
            ascending,
            group,
            search,
        },
    }).then((resp) => {
        if (!resp) return resp;
        const { data, ...rest } = resp;
        return {
            ...rest,
            data: data.map(({ data: itemData, ...item }) => ({
                ...item,
                ...itemData,
            })),
        };
    });
};

export const getFlavorGroupsByCategory = async ({
    category,
    country,
    order_by,
    ascending,
    dataSource,
}) => {
    if (!validDataSources.includes(dataSource)) {
        throw new Error(`Invalid data source: ${dataSource}`);
    }
    return cachedFetch(endpointWrapper)({
        domain: `${dataSource}/flavors/groups`,
        data: {
            category,
            country,
            order_by,
            ascending,
        },
    });
};

export const getFlavorCategories = async ({
    country,
    category,
    flavor,
    order_by,
    ascending,
    dataSource,
}) => {
    return cachedFetch(endpointWrapper)({
        domain: `${dataSource}/flavors/categories`,
        data: {
            category,
            country,
            flavor,
            order_by,
            ascending,
        },
    }).then((resp) => {
        if (resp?.data?.length) {
            resp.data.forEach((item) => {
                item.hierarchies = item?.canonical_hierarchy?.map(
                    ({ cid4, clabel1, clabel2, clabel3, clabel4, clabel5 }) => [
                        cid4,
                        {
                            cat1: clabel1,
                            cat2: clabel2,
                            cat3: clabel3,
                            cat4: clabel4,
                            cat5: clabel5,
                        },
                    ],
                );
            });
        }
        return resp;
    });
};

export const getFlavorHashtags = async ({ country, category, flavor }) => {
    return cachedFetch(endpointWrapper)({
        domain: `tiktok/flavors/hashtags`,
        data: {
            category,
            country,
            flavor,
        },
    });
};

export const getFlavorSearchQueries = async ({ country, category, flavor }) => {
    const flavorSearchQueries = await cachedFetch(endpointWrapper)({
        domain: `google/flavors/search-queries`,
        data: {
            category,
            country,
            flavor,
        },
    });
    const searchQueryIds = flavorSearchQueries?.data?.map(({ id }) => id);
    if (!searchQueryIds?.length) {
        return flavorSearchQueries;
    }
    const searchQueries = await fetchKeywords({
        ids: searchQueryIds,
        minimal: true,
    });
    const translationsById = searchQueries?.results?.reduce(
        (acc, { id, google_translations, human_translations }) => {
            acc[id] = {
                translations: shapeTranslations(
                    google_translations,
                    human_translations,
                ),
            };
            return acc;
        },
        {},
    );
    flavorSearchQueries?.data?.forEach((searchQuery) => {
        const maybeTranslations =
            translationsById[searchQuery.id]?.translations;
        if (maybeTranslations) searchQuery.translations = maybeTranslations;
    });
    return flavorSearchQueries;
};

export const getFlavorPosts = async ({
    category,
    country,
    flavor,
    week,
    year,
    rank_type,
}) => {
    return cachedFetch(endpointWrapper)({
        domain: `tiktok/flavors/posts`,
        data: {
            category,
            country,
            flavor,
            week,
            year,
            rank_type,
        },
    }).then(shapePosts);
};
